import { registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import fr from '@angular/common/locales/fr';
import { NgModule, LOCALE_ID, ErrorHandler } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MatDialogModule,
  MatDividerModule,
  MatExpansionModule,
  MatGridListModule,
  MatIconModule,
  MatListModule,
  MatMenuModule,
  MatProgressSpinnerModule,
  MatSnackBarModule
} from '@angular/material';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule, BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule, Routes } from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fad } from '@fortawesome/pro-duotone-svg-icons';
import { NgxLittleworkerFrameworkModule } from '@little-worker/ngx-littleworker-framework';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { NgxStickySidebarModule } from '@smip/ngx-sticky-sidebar';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { SlideshowModule } from 'ng-simple-slideshow';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { NgxGalleryModule } from 'ngx-gallery';
import { LightboxModule } from 'ngx-lightbox';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxGaugeModule } from 'ngx-gauge';
import { createErrorHandler, TraceService } from '@sentry/angular';

import { MaterialsGuard } from '@auth/materials.guard';
import { AppComponent } from '@components/app.component';
import { AppRoutingModule } from '@components/app-routing.module';
import { BalanceComponent } from '@components/balance/balance.component';
import { ChapterComponent } from '@components/chapter/chapter.component';
import { ConstructionSiteComponent } from '@components/construction-site/construction-site.component';
import { ConstructionSiteListComponent } from '@components/construction-site-list/construction-site-list.component';
import { ConstructionSiteRemarkComponent } from '@components/construction-site-remark/construction-site-remark.component';
import { LoaderComponent } from '@components/CustomComponents/loader/loader.component';
import { DashboardComponent } from '@components/dashboard/dashboard.component';
import { FilesComponent } from '@components/files/files.component';
import { HeaderComponent } from '@components/header/header.component';
import { GlobalMaterialsComponent } from '@components/materials/global-materials/global-materials.component';
import { MaterialTimelineComponent } from '@components/materials/material-timeline/material-timeline.component';
import { MenuComponent } from '@components/menu/menu.component';
import { OrdersMaterialsComponent } from '@components/materials/orders-materials/orders-materials.component';
import { QuotationComponent } from '@components/quotation/quotation.component';
import { QuotationsListComponent } from '@components/quotations-list/quotations-list.component';
import { ScheduleComponent } from '@components/schedule/schedule.component';
import { ServiceComponent } from '@components/service/service.component';
import { ShowroomsComponent } from '@components/showrooms/showrooms.component';
import { ErrorComponent } from '@errors/error.component';
import { GlobalDataService } from '@providers/global-data/global-data.service';

const appRoutes: Routes = [
  { path: 'projects/:pid/quotations', component: QuotationsListComponent },
  {
    path: 'projects/:pid/materials',
    component: GlobalMaterialsComponent,
    children: [
      {
        path: '',
        canActivateChild: [MaterialsGuard],
        children: [
          { path: 'orders', component: OrdersMaterialsComponent }
        ]
      }
    ]
  },
  {
    path: 'quotations/:qid/versions/:vid',
    component: QuotationComponent
  },
  { path: '', component: DashboardComponent },
  { path: 'projects/:pid/dashboard', component: DashboardComponent },
  {
    path: 'projects/:pid',
    redirectTo: 'projects/:pid/dashboard',
    pathMatch: 'full'
  },
  { path: 'projects/:pid/files', component: FilesComponent },
  { path: 'projects/:pid/schedule', component: ScheduleComponent },
  { path: 'projects/:pid/showrooms', component: ShowroomsComponent },
  { path: 'projects/:pid/balance', component: BalanceComponent },
  { path: 'projects/:pid/chantier', component: ConstructionSiteComponent },
  { path: '**', redirectTo: 'projects/:pid/dashboard' }
];

registerLocaleData(fr, 'fr');
// Retrieve access token
export function tokenGetter() {
  return localStorage.getItem('access_token');
}
export const whiteRegex = [new RegExp('^.*$')] as RegExp[];
export function jwtOptionsFactory() {
  return {
    tokenGetter,
    whitelistedDomains: whiteRegex,
    blacklistedRoutes: ['http://picasaweb.google.com/data/entry/api/user/']
  };
}

@NgModule({
  declarations: [
    AppComponent,
    BalanceComponent,
    ChapterComponent,
    ConstructionSiteComponent,
    ConstructionSiteListComponent,
    ConstructionSiteRemarkComponent,
    DashboardComponent,
    ErrorComponent,
    FilesComponent,
    GlobalMaterialsComponent,
    HeaderComponent,
    LoaderComponent,
    MaterialTimelineComponent,
    MenuComponent,
    OrdersMaterialsComponent,
    QuotationComponent,
    QuotationsListComponent,
    ScheduleComponent,
    ServiceComponent,
    ShowroomsComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    MatMenuModule,
    FormsModule,
    NgScrollbarModule,
    MatExpansionModule,
    NoopAnimationsModule,
    BrowserAnimationsModule,
    MatIconModule,
    FontAwesomeModule,
    MatDialogModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatGridListModule,
    MatListModule,
    LightboxModule,
    PdfViewerModule,
    NgxStickySidebarModule,
    NgxGaugeModule,
    SlideshowModule,
    NgxGalleryModule,
    CollapseModule.forRoot(),
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    TabsModule.forRoot(),
    ScrollToModule.forRoot(),
    NgCircleProgressModule.forRoot({
      backgroundColor: '#F1F1F1',
      backgroundPadding: -50,
      radius: 15,
      space: -4,
      maxPercent: 100,
      outerStrokeGradient: false,
      outerStrokeWidth: 6,
      outerStrokeColor: '#00423E',
      innerStrokeColor: '#00423E',
      innerStrokeWidth: 2,
      subtitleFontSize: '7',
      subtitleColor: '#00423E',
      showTitle: false,
      showUnits: false,
      showSubtitle: true,
      showBackground: false,
      showInnerStroke: true,
      startFromZero: false,
      animation: false,
      animateTitle: false,
      animationDuration: 0
    }),
    RouterModule.forRoot(appRoutes, {
      enableTracing: false,
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload'
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory
      }
    }),
    AppRoutingModule,
    NgxLittleworkerFrameworkModule
  ],
  providers: [
    GlobalDataService,
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false
      })
    },
    {
      provide: TraceService,
      deps: [Router]
    },
    { provide: LOCALE_ID, useValue: 'fr' }
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
  constructor(trace: TraceService) {
    library.add(fal, fas, far, fad);
  }
}
